import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDateTime } from "@/components/DataTable";
import { toast } from "sonner";
import { PhoneCall, Paperclip, Search, ChevronDown, ChevronRight, Pencil, AlertTriangle, Cog, ShieldCheck, Download } from "lucide-react";

const DIRECTIONS = ["Inbound", "Outbound", "Internal"];

export function LogContactModal({ open, onOpenChange, partyId, types, onSaved, presetRelated }) {
  const [activityType, setActivityType] = useState("Outbound Phone Call");
  const [direction, setDirection] = useState("Outbound");
  const [subject, setSubject] = useState("");
  const [notes, setNotes] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setSubject(""); setNotes(""); setFollowUp(""); setFiles([]); }
  }, [open]);

  const save = async () => {
    if (!subject.trim()) { toast.error("Enter a subject."); return; }
    setBusy(true);
    try {
      const documentIds = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("party_id", partyId);
        fd.append("category", "Client Correspondence");
        const { data } = await api.post("/dropbox/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data?.id) documentIds.push(data.id);
      }
      const { data } = await api.post("/chronology/log-contact", {
        party_id: partyId, activity_type: activityType, direction, subject: subject.trim(),
        notes, follow_up_date: followUp, document_ids: documentIds,
        related_entity_type: presetRelated?.type || "", related_entity_id: presetRelated?.id || "",
      });
      toast.success("Contact logged to Agency Chronology.");
      onOpenChange(false);
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="log-contact-modal">
        <DialogHeader>
          <DialogTitle>Log Contact</DialogTitle>
          <DialogDescription>Record an interaction into the permanent Agency Chronology.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1 block">Activity type</Label>
              <Select value={activityType} onValueChange={setActivityType}>
                <SelectTrigger data-testid="lc-type"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {(types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Direction</Label>
              <Select value={direction} onValueChange={setDirection}>
                <SelectTrigger data-testid="lc-direction"><SelectValue /></SelectTrigger>
                <SelectContent>{DIRECTIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs mb-1 block">Subject</Label>
            <Input data-testid="lc-subject" value={subject} autoFocus onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Renewal discussion" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Notes (full narrative)</Label>
            <Textarea data-testid="lc-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={7}
              placeholder="Type detailed notes during or right after the call — what was discussed, requested, promised, agreed, observed, and any follow-up." />
          </div>
          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <Label className="text-xs mb-1 block">Follow-up date (optional)</Label>
              <Input data-testid="lc-followup" type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs mb-1 block flex items-center gap-1"><Paperclip className="h-3 w-3" /> Attach files (optional)</Label>
              <Input data-testid="lc-files" type="file" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} />
            </div>
          </div>
          {files.length > 0 && <p className="text-xs text-mutedsea">{files.length} file(s) will be filed in the cabinet.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button data-testid="lc-save" onClick={save} disabled={busy} className="bg-atlantic hover:bg-ocean text-white">{busy ? "Saving…" : "Save to Chronology"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditContactModal({ entry, onClose, types, onSaved }) {
  const [subject, setSubject] = useState(entry?.subject || "");
  const [notes, setNotes] = useState(entry?.notes || "");
  const [activityType, setActivityType] = useState(entry?.activity_type || "Internal Note");
  const [direction, setDirection] = useState(entry?.direction || "Outbound");
  const [followUp, setFollowUp] = useState(entry?.follow_up_date || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!subject.trim()) { toast.error("Enter a subject."); return; }
    setBusy(true);
    try {
      await api.put(`/chronology/${entry.id}`, {
        subject: subject.trim(), notes, activity_type: activityType, direction, follow_up_date: followUp,
      });
      toast.success("Chronology entry updated (prior version retained).");
      onClose();
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="chrono-edit-modal">
        <DialogHeader>
          <DialogTitle>Edit chronology entry</DialogTitle>
          <DialogDescription>Entries are permanent — the previous values are preserved in the edit history.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1 block">Activity type</Label>
              <Select value={activityType} onValueChange={setActivityType}>
                <SelectTrigger data-testid="ce-type"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{(types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Direction</Label>
              <Select value={direction} onValueChange={setDirection}>
                <SelectTrigger data-testid="ce-direction"><SelectValue /></SelectTrigger>
                <SelectContent>{DIRECTIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-xs mb-1 block">Subject</Label>
            <Input data-testid="ce-subject" value={subject} onChange={(e) => setSubject(e.target.value)} /></div>
          <div><Label className="text-xs mb-1 block">Notes</Label>
            <Textarea data-testid="ce-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={6} /></div>
          <div><Label className="text-xs mb-1 block">Follow-up date (optional)</Label>
            <Input data-testid="ce-followup" type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button data-testid="ce-save" onClick={save} disabled={busy} className="bg-atlantic hover:bg-ocean text-white">{busy ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function badgeClass(t = "") {
  if (t.includes("Phone") || t.includes("Voicemail")) return "bg-atlantic/10 text-atlantic border-atlantic/30";
  if (t.includes("Email")) return "bg-ocean/10 text-ocean border-ocean/30";
  if (t.includes("Claim")) return "bg-seared/10 text-seared border-seared/30";
  if (t.includes("Note")) return "bg-cloud text-mutedsea border-border";
  return "bg-seagreen/10 text-seagreen border-seagreen/30";
}

export default function AgencyChronology({ partyId, presetRelated, reloadKey, canWrite = true }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [types, setTypes] = useState([]);
  const [q, setQ] = useState("");
  const [ftype, setFtype] = useState("");
  const [fdir, setFdir] = useState("");
  const [showLog, setShowLog] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const pageSize = 25;

  useEffect(() => { api.get("/chronology/activity-types").then(({ data }) => setTypes(data.types || [])).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    const params = { page, page_size: pageSize };
    if (q) params.q = q;
    if (ftype) params.type = ftype;
    if (fdir) params.direction = fdir;
    if (presetRelated?.id) { params.related_entity_type = presetRelated.type; params.related_entity_id = presetRelated.id; }
    try {
      const { data } = await api.get(`/parties/${partyId}/chronology`, { params });
      setItems(data.items || []); setTotal(data.total || 0);
    } catch (e) {
      setError(true); setItems([]); setTotal(0);
    } finally { setLoading(false); }
  }, [partyId, page, q, ftype, fdir, presetRelated]);

  useEffect(() => { if (partyId) load(); }, [load, reloadKey]);

  const exportChrono = async (fmt) => {
    try {
      const res = await api.get(`/parties/${partyId}/chronology/export`, { params: { format: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `chronology_${String(partyId).slice(0, 8)}_${new Date().toISOString().replace(/[:.]/g, "").slice(0, 15)}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported chronology (${fmt.toUpperCase()}).`);
    } catch (e) { toast.error("Export failed."); }
  };

  return (
    <div data-testid="agency-chronology">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {canWrite && (
          <Button data-testid="log-contact-btn" onClick={() => setShowLog(true)} size="sm" className="bg-atlantic hover:bg-ocean text-white">
            <PhoneCall className="h-4 w-4 mr-1.5" /> Log Contact
          </Button>
        )}
        <div className="relative flex-1 min-w-[180px]">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-mutedsea" />
          <Input data-testid="chrono-search" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search subject & notes…" className="pl-8 h-9" />
        </div>
        <Select value={ftype || "all"} onValueChange={(v) => { setPage(1); setFtype(v === "all" ? "" : v); }}>
          <SelectTrigger className="w-44 h-9" data-testid="chrono-filter-type"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent className="max-h-72"><SelectItem value="all">All types</SelectItem>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fdir || "all"} onValueChange={(v) => { setPage(1); setFdir(v === "all" ? "" : v); }}>
          <SelectTrigger className="w-32 h-9" data-testid="chrono-filter-dir"><SelectValue placeholder="Direction" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem>{DIRECTIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
        <Button data-testid="chrono-export-csv" size="sm" variant="outline" onClick={() => exportChrono("csv")} className="border-atlantic text-atlantic h-9"><Download className="h-3.5 w-3.5 mr-1" />CSV</Button>
        <Button data-testid="chrono-export-json" size="sm" variant="outline" onClick={() => exportChrono("json")} className="border-atlantic text-atlantic h-9"><Download className="h-3.5 w-3.5 mr-1" />JSON</Button>
      </div>

      {loading ? (
        <p className="text-sm text-mutedsea py-8 text-center" data-testid="chrono-loading">Loading chronology…</p>
      ) : error ? (
        <div className="text-sm text-seared py-8 text-center flex flex-col items-center gap-2" data-testid="chrono-error">
          <AlertTriangle className="h-5 w-5" /> Could not load the chronology.
          <Button size="sm" variant="outline" onClick={load}>Retry</Button>
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-mutedsea py-8 text-center">No chronology entries yet.{canWrite && <> Click <strong>Log Contact</strong> to record the first interaction.</>}</p>
      ) : (
        <div className="space-y-2">
          {items.map((a) => {
            const isOpen = expanded[a.id];
            const isSystem = a.source === "System";
            return (
              <div key={a.id} className="bg-white border border-border rounded-sm p-3" data-testid={`chrono-entry-${a.id}`}>
                <div className="flex items-start gap-3">
                  <button className="mt-0.5" onClick={() => setExpanded((s) => ({ ...s, [a.id]: !s[a.id] }))}>
                    {isOpen ? <ChevronDown className="h-4 w-4 text-mutedsea" /> : <ChevronRight className="h-4 w-4 text-mutedsea" />}
                  </button>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded((s) => ({ ...s, [a.id]: !s[a.id] }))}>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className={badgeClass(a.activity_type)}>{a.activity_type}</Badge>
                      {a.direction && a.direction !== "System" && <Badge variant="outline" className="bg-cloud text-slate border-border">{a.direction}</Badge>}
                      {isSystem && <Badge variant="outline" className="bg-cloud text-mutedsea border-border" title="Automatically recorded"><Cog className="h-3 w-3 mr-1" />Auto</Badge>}
                      <span className="font-medium text-slate truncate">{a.subject}</span>
                      {a.document_ids?.length > 0 && <Paperclip className="h-3.5 w-3.5 text-atlantic" title={`${a.document_ids.length} attachment(s)`} />}
                      {a.edited && <span className="text-xs text-mutedsea italic">(edited)</span>}
                    </div>
                    <div className="text-xs text-mutedsea mt-0.5 flex items-center gap-1.5">
                      <span>{fmtDateTime(a.timestamp)} · {a.owner || "—"}{a.related_entity_type ? ` · ${a.related_entity_type}` : ""}</span>
                      {a.entry_hash && <ShieldCheck data-testid={`chrono-seal-${a.id}`} className="h-3 w-3 text-seagreen" title={`Digitally sealed ${a.sealed_at || ""} · #${a.ledger_seq || ""} · ${String(a.entry_hash).slice(0, 12)}…`} />}
                    </div>
                    {isOpen && a.notes && <p className="text-sm text-slate whitespace-pre-wrap mt-2 border-t border-border pt-2">{a.notes}</p>}
                    {isOpen && a.follow_up_date && <p className="text-xs text-atlantic mt-1">Follow-up: {a.follow_up_date}</p>}
                    {isOpen && a.edit_history?.length > 0 && (
                      <div className="mt-2 border-t border-border pt-2" data-testid={`chrono-history-${a.id}`}>
                        <p className="text-xs font-medium text-mutedsea mb-1">Edit history ({a.edit_history.length})</p>
                        {a.edit_history.map((h, i) => (
                          <div key={i} className="text-xs text-mutedsea">
                            {fmtDateTime(h.edited_at)} · {h.edited_by} — prior subject: “{h.subject}”
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {canWrite && !isSystem && (
                    <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" title="Edit entry"
                      data-testid={`chrono-edit-${a.id}`} onClick={() => setEditEntry(a)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
          {total > pageSize && (
            <div className="flex items-center justify-between pt-2 text-sm">
              <span className="text-mutedsea">{total} entries</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
                <Button size="sm" variant="outline" disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </div>
      )}

      <LogContactModal open={showLog} onOpenChange={setShowLog} partyId={partyId} types={types}
        presetRelated={presetRelated} onSaved={() => { setPage(1); load(); }} />
      {editEntry && <EditContactModal entry={editEntry} types={types} onClose={() => setEditEntry(null)} onSaved={load} />}
    </div>
  );
}
